import { useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "./kpiFormat";

export const LEAD_DRILLS = new Set(["meta_paid_leads", "organic_leads", "cost_per_lead"]);
const label = (r) => r.client_name || r.client_email || r.client_id;

/** Mark a lead in the KPI list as the same person as another lead (or an email / phone typed in). */
export const SamePersonDialog = ({ week, row, rows, onClose, onLinked }) => {
  const others = rows.filter((r) => r.client_id !== row.client_id);
  const [to, setTo] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const t = typed.trim();
  const digits = t.replace(/\D/g, "");
  const typedOk = !t || (t.includes("@") ? /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t) : digits.length >= 8);
  const target = t ? { client_id: t.includes("@") ? t.toLowerCase() : `mobile:${digits.slice(-9)}`, client_name: "" } : to;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kpi/client-links", { from_id: row.client_id, to_id: target.client_id, from_name: label(row),
        to_name: target.client_name || target.client_email || "", week });
      toast.success("Linked as the same person", { description: r.data.rebuilt ? "This week's draft was recounted." : "Refresh the draft to recount." });
      onLinked();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="kpi-same-person-dialog">
        <DialogHeader>
          <DialogTitle>Same person as…</DialogTitle>
          <DialogDescription>
            <b>{label(row)}</b> ({fmtDateTime(row.date)}) is the same person as the lead you pick. They'll count as one lead in every
            week, and their purchases are joined up. You can undo this under Ad spend &amp; data → Same-person links.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-60 space-y-1.5 overflow-auto">
          {others.map((r, i) => (
            <button key={`${r.client_id}-${i}`} type="button" onClick={() => { setTo(r); setTyped(""); }}
              data-testid={`kpi-same-person-pick-${i}`}
              className={`w-full rounded-md border p-2 text-left text-sm transition-colors ${to === r && !typed ? "border-blue-500 bg-blue-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
              <span className="font-medium text-zinc-900">{label(r)}</span>
              <span className="ml-2 text-xs text-zinc-500">{r.client_email || ""} · {fmtDateTime(r.date)} · {r.channel}</span>
            </button>
          ))}
        </div>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="…or type their email or mobile number"
          data-testid="kpi-same-person-typed" />
        {!typedOk && <p className="text-xs text-red-600" data-testid="kpi-same-person-typed-error">Enter a full email address or mobile number</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!target?.client_id || !typedOk || busy} data-testid="kpi-same-person-save">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
